import React, { useState, useEffect } from 'react';
import { DEFAULT_FIT, DEFAULT_FOCUS_X, DEFAULT_FOCUS_Y } from '../lib/fallback';

// Enhetlig bildkomponent.
// - DB-bilder: skicka `url` (färdig publik URL).
// - Statisk fallback: skicka `basePath` + `filename` (utan ändelse) så provas
//   .jpeg/.jpg/.png i tur och ordning.
// Passning styrs av `fit` ('cover' | 'contain') och fokuspunkt `focusX`/`focusY` (0–100).
export const ManagedImage = ({
  url,
  basePath,
  filename,
  alt,
  className,
  style,
  fit = DEFAULT_FIT,
  focusX = DEFAULT_FOCUS_X,
  focusY = DEFAULT_FOCUS_Y,
  onError,
}) => {
  const [imageSrc, setImageSrc] = useState(url || '');
  const [loaded, setLoaded] = useState(Boolean(url));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // Direkt URL (från databasen) – använd som den är.
    if (url) {
      setImageSrc(url);
      setLoaded(true);
      setFailed(false);
      return;
    }
    // Statisk fallback – prova filändelser.
    if (!basePath || !filename) return;
    const formats = ['.jpeg', '.jpg', '.png'];
    let i = 0;
    let mounted = true;
    setLoaded(false);
    setFailed(false);

    const tryNext = () => {
      if (!mounted) return;
      if (i >= formats.length) {
        setFailed(true);
        if (onError) onError();
        return;
      }
      const testSrc = `${basePath}${filename}${formats[i]}`;
      const img = new Image();
      img.onload = () => {
        if (mounted) {
          setImageSrc(testSrc);
          setLoaded(true);
        }
      };
      img.onerror = () => {
        if (mounted) {
          i += 1;
          tryNext();
        }
      };
      img.src = testSrc;
    };
    tryNext();
    return () => {
      mounted = false;
    };
  }, [url, basePath, filename, onError]);

  const fitStyle = {
    objectFit: fit,
    objectPosition: `${focusX}% ${focusY}%`,
    ...style,
  };

  if (!loaded && !failed) {
    return (
      <div className={`${className || ''} bg-cv-panel flex items-center justify-center`} style={style}>
        <div className="w-5 h-5 border border-cv-edge2 border-t-cv-accent rounded-full animate-spin" />
      </div>
    );
  }

  if (failed) {
    return (
      <div className={`${className || ''} bg-cv-panel flex items-center justify-center`} style={style}>
        <span className="text-cv-faint text-xs">Bild saknas</span>
      </div>
    );
  }

  return <img src={imageSrc} alt={alt} className={className} style={fitStyle} loading="lazy" onError={onError} />;
};
